// LA FRANJA DE ARRIBA DEL CRM, para las tres cosas que tienen que ver con el
// plan de Deskte, en este orden:
//
//   0. Tu cuenta está en modo solo lectura — la agencia está suspendida.
//   1. No pudimos cobrar tu tarjeta — a quien se le cayó el cobro en Stripe.
//   2. Recuerda activar tu plan  — para quien nunca se suscribió.
//   3. Tu prueba termina mañana  — un solo día.
//
// ES UN SOLO SITIO A PROPÓSITO. Antes esto era `TrialBanner` y solo sabía de la
// prueba. Dos franjas hablando del plan a la vez no las lee nadie: se quedan en
// una, y la de más arriba gana.
//
// EL TONO LO ESCRIBIÓ LISSETTE, palabra por palabra (2026-09-13): *"quiero que
// sea algo como **recuerda activar tu plan para seguir disfrutando de tu
// servicio**"*. Es un recordatorio, no un cobro, y por eso:
//   - NO es rojo. Azul, el color de la marca, no el de una alarma.
//   - NO dice "no has pagado", ni "pendiente", ni "vencido". A quien lleva
//     meses usando Deskte gratis nadie le ha cobrado nunca (E-222: un aviso
//     tiene que ser verdad).
//   - NO amenaza con quitar nada, porque no quita nada: mientras la agencia no
//     esté suspendida a mano desde Platform Admin, este recordatorio es solo
//     un recordatorio y se sigue pudiendo trabajar igual. El corte —el modo
//     solo lectura de más abajo— es una decisión aparte y deliberada de
//     Lissette, agencia por agencia, no una consecuencia automática de este
//     aviso. (Hasta el 2026-09-21 aquí decía "nada se corta" a secas; con el
//     modo solo lectura eso ya no vale como frase suelta — E-307.)
//   - Se cierra y NO vuelve hasta la semana siguiente. Una versión anterior
//     volvía cada día; eso ya no es recordar, es insistir.
//
// A QUIÉN LE SALE: solo a la persona a la que se le cobra —la dueña de la
// agencia, la titular de un plan Solo, o la única agente si la cuenta no tiene
// ninguna de las dos—. Lo decide el SERVIDOR (`isBillingContact`, calculado con
// el mismo `contactoDeCobro` que usa Platform Admin para mandar la factura), no
// esta pantalla. Preguntar aquí por el rol es exactamente lo que rompió tres
// pantallas seguidas los días 10 y 11 (E-220, E-221, E-222): una cuenta Solo
// que viene de Agency puede tener a su única persona como `agent`.
//
// EL AVISO DE TARJETA RECHAZADA SE QUITÓ Y VOLVIÓ. Se programó, Lissette lo
// descartó (2026-09-12, *"quítalos los dos"*) porque un cobro caído se avisaba
// por fuera —correo o WhatsApp desde Platform Admin—, y volvió el 2026-09-25 al
// descubrirse que ese "por fuera" no existía: Stripe no le manda los cobros
// fallidos (*"mucha no deja enviarla a el correo como los fallidos"*) y Deskte
// tampoco mandaba nada. El razonamiento largo está en `utils/avisoDePago.ts`.
//
// De aquella decisión se conserva lo que importaba, y son las dos reglas que
// hacen que esta franja no sea lo que se rechazó entonces:
//   - **No es roja.** Ámbar, como la prueba. El rojo sigue reservado al modo
//     solo lectura, que es el único aviso que describe algo que impide
//     trabajar ahora mismo. Un cobro caído no corta nada en Deskte.
//   - **Solo la ve quien tiene la tarjeta** (`isBillingContact`, del
//     servidor). Un agente no puede arreglar la tarjeta de su dueña; meterle
//     ese susto sería el "franja roja dentro del CRM del agente" que se
//     rechazó.
//
// Fuente: GET /billing/products → `current.readOnly` / `paymentIssue` /
// `isBillingContact` / `inTrial` / `daysLeft`, todo calculado en el servidor.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { createBillingPortal, listBillingProducts } from '../api.js';
import { toast } from './Toast.jsx';

const DISMISS_KEY = 'deskte:trialBannerDismissedAt';
const DISMISS_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours — back the next morning

// EL RECORDATORIO DEL PLAN SALE UNA VEZ AL DÍA, mientras no pague. Se le da a
// Continuar y no vuelve hasta el día siguiente.
//
// Empezó siendo una semana y Lissette lo corrigió (2026-09-13): *"por qué hasta
// la semana que viene, que salga diario mientras no pague"*. Tenía razón: a los
// siete días ya no es un recordatorio, es un olvido.
//
// SE GUARDA EL DÍA, NO LA HORA. Con un plazo de 24 horas, quien lo cierra a las
// 9:00 no lo vuelve a ver hasta las 9:00 del día siguiente — así que si entra a
// las 8:00 se salta un día entero sin verlo. Guardando la fecha sale la PRIMERA
// vez que abre Deskte cada día, que es lo que significa "diario".
//
// Y el día es el LOCAL, nunca `toISOString()`: en Florida eso adelanta la fecha
// a partir de las 7 u 8 de la noche y el aviso reaparecería esa misma tarde.
// (Es la regla de la casa sobre fechas, cobrada aquí.)
const PLAN_DISMISS_KEY = 'deskte:recordatorioDePlanVistoEl';

// El cobro caído se cierra por DÍA igual que el recordatorio del plan, con su
// propia clave: compartirla haría que cerrar uno escondiera el otro, y son dos
// cosas distintas. Vuelve cada día mientras la tarjeta siga sin pasar — que es
// exactamente mientras haya algo que hacer.
const COBRO_DISMISS_KEY = 'deskte:avisoDeCobroCaidoVistoEl';

function hoyLocal() {
  const d = new Date();
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
}

// El rojo es SOLO para el modo solo lectura, y ahí sí está ganado: no es un
// recordatorio, es un hecho — ahora mismo no se puede guardar nada. Para el
// resto de avisos del plan sigue prohibido (ver arriba).
const ROJO = {
  bg:     'rgba(239,68,68,.10)',
  border: 'rgba(239,68,68,.45)',
  text:   'var(--c-white,#eef6ff)',
  muted:  'var(--c-text2,#7aa3c0)',
  button: '#ef4444',
};

const AMBAR = {
  bg:     'rgba(255,183,0,.08)',
  border: 'rgba(255,183,0,.4)',
  text:   'var(--c-white,#eef6ff)',
  muted:  'var(--c-text2,#7aa3c0)',
  button: 'var(--c-amber,#ffb700)',
};

export function BillingBanner({ currentUser, lang, onUpgradeClick }) {
  const es = lang === 'es';
  const [info, setInfo] = useState(null);
  const [dismissed, setDismissed] = useState(() => sigueCerrado(DISMISS_KEY, DISMISS_TTL_MS, 'session'));
  const [planCerrado, setPlanCerrado] = useState(() => yaSeVioHoy(PLAN_DISMISS_KEY));
  const [cobroCerrado, setCobroCerrado] = useState(() => yaSeVioHoy(COBRO_DISMISS_KEY));
  const [abriendoPortal, setAbriendoPortal] = useState(false);

  // Lleva al portal de Stripe, donde se cambia la tarjeta. Es el MISMO camino
  // que el botón de la pestaña de Facturación (`BillingTab#handlePortal`), no
  // uno nuevo.
  //
  // SI STRIPE NO CONTESTA, EL BOTÓN NO SE QUEDA MUERTO: se dice qué pasó y se
  // la lleva a su pestaña de Facturación, donde están todos los demás caminos
  // de pago. Un aviso que pide actuar y luego no deja actuar es peor que no
  // avisar (regla de oro 42).
  const abrirPortalDeStripe = async () => {
    setAbriendoPortal(true);
    try {
      const { url } = await createBillingPortal();
      if (url) { window.location.assign(url); return; }
      throw new Error('Stripe no devolvió una dirección');
    } catch (e) {
      toast.error((es ? 'No se pudo abrir Stripe: ' : 'Could not open Stripe: ') + (e.message || ''));
      if (onUpgradeClick) onUpgradeClick();
    } finally {
      setAbriendoPortal(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (!currentUser || currentUser.isPlatformOwner) {
      // La dueña de la plataforma no se cobra a sí misma. Ni se pregunta.
      setInfo({ inTrial: false });
      return () => { cancelled = true; };
    }
    // LOS ASIENTOS SÍ PREGUNTAN, y eso cambió el 2026-09-21. Antes se saltaban
    // la llamada entera porque usan el plan de quien los supervisa y no tienen
    // nada que decidir sobre él. Pero el modo solo lectura les cae encima
    // igual: a un asiento que de pronto no puede guardar una ficha hay que
    // decirle por qué, o abre un ticket. Lo que NO les sale es el recordatorio
    // del plan ni el de la prueba — de eso se encargan `isBillingContact`
    // (que el servidor les pone en false) y el filtro de más abajo.
    listBillingProducts()
      .then((data) => {
        if (cancelled) return;
        setInfo({
          inTrial: !!data.current?.inTrial,
          daysLeft: data.current?.daysLeft ?? 0,
          paymentIssue: data.current?.paymentIssue ?? null,
          isBillingContact: !!data.current?.isBillingContact,
          readOnly: !!data.current?.readOnly,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setInfo({ inTrial: false });
      });
    return () => { cancelled = true; };
  }, [currentUser]);

  if (!info) return null;

  // ── 0 · Modo solo lectura ──────────────────────────────────────────────
  //
  // Va PRIMERO y gana a todo lo demás: es lo único de esta lista que describe
  // algo que está pasando ahora mismo en la pantalla. Recordarle a alguien que
  // active su plan mientras no puede guardar una ficha sería contestar a la
  // pregunta equivocada.
  //
  // LE SALE A TODA LA AGENCIA, no solo a quien paga — al revés que el
  // recordatorio del plan. Un agente al que el servidor le rechaza cada
  // guardado necesita saber por qué aunque la tarjeta no sea suya; lo que no
  // puede es quedarse mirando un error sin explicación.
  //
  // Y NO SE CIERRA. No hay × ni "Continuar": mientras la cuenta esté en solo
  // lectura, el aviso está. Cerrarlo dejaría la pantalla exactamente igual que
  // una que funciona, y no funciona.
  if (info.readOnly) {
    // A UN ASIENTO NO SE LE OFRECE EL BOTÓN DE PAGAR. El plan no es suyo y
    // "Ver mi plan" lo llevaría a una pestaña donde no puede hacer nada —
    // mandarlo ahí es peor que no ofrecerle nada. A él se le dice qué pasa y
    // a quién preguntar; el botón es para quien tiene la tarjeta.
    const esAsiento = currentUser?.role === 'assistant';
    return (
      <Franja
        colores={ROJO}
        icono="🔒"
        titulo={es
          ? 'Tu cuenta está en modo solo lectura.'
          : 'Your account is in read-only mode.'}
        detalle={esAsiento
          ? (es
            ? 'Puedes ver y exportar toda la información, pero no crear ni editar. Avisa a la persona que maneja el plan de tu agencia.'
            : 'You can view and export everything, but not create or edit. Let whoever manages your agency plan know.')
          : (es
            ? 'Puedes ver y exportar toda tu información, pero no crear ni editar. Activa tu plan y recuperas el acceso completo.'
            : 'You can view and export everything, but not create or edit. Activate your plan to get full access back.')}
        boton={esAsiento ? null : (es ? 'Ver mi plan →' : 'View my plan →')}
        onBoton={esAsiento ? null : onUpgradeClick}
        es={es}
      />
    );
  }

  // Desde aquí para abajo, nada para los asientos: preguntan al servidor solo
  // por el aviso de arriba (ver el efecto).
  if (currentUser?.role === 'assistant') return null;

  // ── 1 · No pudimos cobrar tu tarjeta ───────────────────────────────────
  //
  // Va antes del recordatorio del plan porque es lo contrario de él: aquí SÍ
  // se intentó cobrar y SÍ falló. Nunca salen los dos —`paymentIssue` es un
  // solo valor—, pero el orden deja escrito cuál manda si algún día dejara de
  // serlo.
  //
  // LA FRASE ES VERDAD, que es la regla que dejó E-222: a esta agencia se le
  // pasó la tarjeta y la tarjeta no pasó. Y no promete ni amenaza con nada:
  // en Deskte un cobro caído no corta el servicio; el corte es el modo solo
  // lectura, que Lissette enciende a mano, agencia por agencia.
  if (info.paymentIssue === 'PAYMENT_FAILED' && info.isBillingContact && !cobroCerrado) {
    return (
      <Franja
        colores={AMBAR}
        icono="💳"
        titulo={es
          ? 'No pudimos cobrar tu tarjeta.'
          : "We couldn't charge your card."}
        detalle={es
          ? 'Actualízala para que tu plan siga al día. Mientras tanto no se te corta nada.'
          : 'Update it to keep your plan current. Nothing is cut off in the meantime.'}
        boton={abriendoPortal
          ? (es ? 'Abriendo…' : 'Opening…')
          : (es ? 'Actualizar tarjeta →' : 'Update card →')}
        onBoton={abriendoPortal ? null : abrirPortalDeStripe}
        onCerrar={() => {
          marcarVistoHoy(COBRO_DISMISS_KEY);
          setCobroCerrado(true);
        }}
        es={es}
      />
    );
  }

  // ── 2 · Recuerda activar tu plan ───────────────────────────────────────
  //
  // VENTANA EMERGENTE, NO UNA FRANJA. Empezó siendo una franja fina arriba y
  // Lissette la vio y dijo que no bastaba (2026-09-13): *"no se ve tan grande,
  // me gustaría que saliera como un pop pop y que para que se vaya tenga que
  // darle continuar"*. Una franja se lee de reojo y se ignora; esto pide un
  // clic, y ese clic es justo el punto — no se cierra sola ni por descuido.
  //
  // `isBillingContact` manda: un agente de una agencia grande no tiene por qué
  // enterarse del plan de su dueña.
  if (info.paymentIssue === 'NOT_SUBSCRIBED' && info.isBillingContact && !planCerrado) {
    return (
      <VentanaDelPlan
        es={es}
        onContinuar={() => {
          marcarVistoHoy(PLAN_DISMISS_KEY);
          setPlanCerrado(true);
        }}
      />
    );
  }

  // ── 3 · La prueba, exactamente el día antes de que termine ─────────────
  //
  // Corría durante toda la prueba con un degradado cian → ámbar → rojo. Eso era
  // ruido: a quien está en el día 1 de 7 no le hace falta una cuenta atrás, y
  // una franja que está siempre deja de leerse justo cuando importa. Lissette
  // pidió solo el "termina mañana" (2026-08-10).
  //
  // No amenaza con perder el acceso a propósito: nada en Deskte bloquea una
  // agencia cuando se le acaba la prueba, y quien se inscribió por Stripe ya
  // tiene tarjeta y simplemente se le cobra ese día.
  if (!info.inTrial || dismissed) return null;
  // `daysLeft` viene redondeado hacia arriba del servidor: 1 es "termina
  // mañana". 0 sería "termina hoy", y ahí ya se calla.
  if (info.daysLeft !== 1) return null;

  return (
    <Franja
      colores={AMBAR}
      icono="⏰"
      titulo={es ? 'Tu prueba gratis termina mañana.' : 'Your free trial ends tomorrow.'}
      detalle={es
        ? 'Revisa tu plan cuando quieras — sigues teniendo acceso a todo.'
        : 'Review your plan whenever you like — you keep full access.'}
      boton={es ? 'Ver mi plan →' : 'View my plan →'}
      onBoton={onUpgradeClick}
      onCerrar={() => {
        guardar(DISMISS_KEY, 'session');
        setDismissed(true);
      }}
      es={es}
    />
  );
}

// LA VENTANA DEL PLAN.
//
// Reusa `.dkt-overlay` y `.dkt-modal` —el mismo fondo oscuro con desenfoque,
// el mismo borde y la misma caja que TODAS las ventanas de Deskte, con su
// version clara y sus ajustes de movil ya resueltos—, pero NO usa el
// componente `Modal` de `ui.jsx`: aquel pinta siempre una ✕ en la cabecera, y
// aqui la unica salida tiene que ser el boton. Lo pedido fue literal: *"para
// que se vaya tenga que darle continuar"*.
//
// Por eso tampoco cierra al pulsar fuera. Da la casualidad de que eso ya es la
// regla de la casa —`Modal` lleva desde siempre sin cerrarse por un clic de
// mas, porque los agentes perdian formularios a medio llenar—, asi que aqui no
// se inventa nada: se sigue.
//
// Va por portal a <body>, como las demas: dentro del arbol del contenido
// heredaria el desplazamiento de la pagina y quedaria por debajo de la
// cabecera pegajosa.
function VentanaDelPlan({ es, onContinuar }) {
  const nodo = (
    <div className="dkt-overlay">
      <div className="dkt-modal" style={{ maxWidth: 420 }}>
        <div className="dkt-mbody" style={{ textAlign: 'center', padding: '1.8rem 1.5rem 1.5rem' }}>
          <div style={{ fontSize: '2.6rem', lineHeight: 1, marginBottom: '.9rem' }}>✨</div>
          <div style={{
            fontFamily: 'var(--font-display)', fontSize: '1.25rem', fontWeight: 800,
            color: 'var(--c-white,#eef6ff)', lineHeight: 1.3, marginBottom: '.5rem',
          }}>
            {es ? 'Recuerda activar tu plan' : 'Remember to activate your plan'}
          </div>
          <div style={{ fontSize: '.86rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.5 }}>
            {es ? 'para seguir disfrutando de tu servicio.' : 'to keep enjoying your service.'}
          </div>
          <button
            type="button" onClick={onContinuar} autoFocus
            className="dkt-btn-p"
            style={{ marginTop: '1.5rem', width: '100%', padding: '.7rem 1.2rem', fontSize: '.85rem' }}
          >
            {es ? 'Continuar' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
  if (typeof document === 'undefined') return nodo;
  return createPortal(nodo, document.body);
}

// La franja fina de arriba. La usan dos avisos: el modo solo lectura (rojo, sin
// ×) y "tu prueba termina mañana" (ámbar, con ×). El recordatorio del plan no,
// que se fue a una ventana emergente — no todo merece parar la pantalla, pero
// eso sí.
function Franja({ colores, icono, titulo, detalle, boton, onBoton, onCerrar, es }) {
  return (
    <div style={{
      background: colores.bg,
      border: `1px solid ${colores.border}`,
      borderRadius: 10,
      padding: '.55rem .85rem',
      margin: '.5rem 0 .85rem',
      display: 'flex',
      alignItems: 'center',
      gap: '.65rem',
      flexWrap: 'wrap',
    }}>
      <span style={{ fontSize: '1rem' }}>{icono}</span>
      <span style={{ fontSize: '.78rem', color: colores.text, flex: 1, lineHeight: 1.4 }}>
        <strong>{titulo}</strong>{' '}
        <span style={{ color: colores.muted }}>{detalle}</span>
      </span>
      <div style={{ display: 'flex', gap: '.4rem' }}>
        {/* Igual que la × de al lado: el botón solo existe si hay algo que
            ofrecer. Un asiento no paga el plan de su agencia. */}
        {boton ? (
          <button
            onClick={onBoton}
            style={{
              background: colores.button,
              color: '#070d14',
              border: 'none',
              padding: '.42rem .9rem',
              borderRadius: 7,
              fontSize: '.74rem',
              fontWeight: 800,
              fontFamily: 'inherit',
              cursor: 'pointer',
              letterSpacing: '.02em',
            }}
          >{boton}</button>
        ) : null}
        {/* La × solo existe si hay a dónde cerrar. El aviso de solo lectura no
            trae `onCerrar` a propósito: mientras la cuenta esté así, está. Sin
            esta condición la × se pintaba igual y no hacía nada al pulsarla,
            que es peor que no tenerla. */}
        {onCerrar ? (
          <button
            onClick={onCerrar}
            aria-label={es ? 'Cerrar' : 'Dismiss'}
            style={{
              background: 'transparent',
              border: 'none',
              color: colores.muted,
              cursor: 'pointer',
              padding: '.2rem .35rem',
              fontSize: '.95rem',
              lineHeight: 1,
            }}
          >×</button>
        ) : null}
      </div>
    </div>
  );
}

// Los dos accesos al almacenamiento van envueltos: en una ventana privada
// `sessionStorage`/`localStorage` pueden lanzar. Si falla la lectura, el
// mensaje simplemente sale; si falla el escritura, la × no se recuerda — feo,
// pero nunca una pantalla en blanco.
function almacen(cual) {
  return cual === 'local' ? localStorage : sessionStorage;
}
function sigueCerrado(clave, ttlMs, cual) {
  try {
    const raw = almacen(cual).getItem(clave);
    if (!raw) return false;
    const cerradoEn = Number(raw);
    if (!Number.isFinite(cerradoEn)) return false;
    return Date.now() - cerradoEn < ttlMs;
  } catch (_) {
    return false;
  }
}
function guardar(clave, cual) {
  try { almacen(cual).setItem(clave, String(Date.now())); } catch (_) {}
}

// El recordatorio del plan no va por plazo sino por DÍA. Si el navegador
// bloquea el almacenamiento, `yaSeVioHoy` devuelve false y el aviso sale: mejor
// enseñarlo de más que dejar de enseñarlo por un fallo de almacenamiento.
function yaSeVioHoy(clave) {
  try { return localStorage.getItem(clave) === hoyLocal(); } catch (_) { return false; }
}
function marcarVistoHoy(clave) {
  try { localStorage.setItem(clave, hoyLocal()); } catch (_) {}
}
